import { api } from "./api";

export interface Badge {
  _id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  rarity?: "common" | "uncommon" | "rare" | "epic" | "legendary";
  requirement?: {
    type: string;
    count: number;
  };
  createdAt: string;
}

export interface UserBadge {
  _id: string;
  badge: Badge;
  earnedAt: string;
}

export interface UserStats {
  totalPoints: number;
  level: number;
  rank: number;
  badgesCount: number;
  coursesCompleted: number;
  quizzesPassed: number;
  streakDays: number;
  totalHoursLearned: number;
  currentStreak?: number;
  longestStreak?: number;
}

export interface LeaderboardEntry {
  rank: number;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  totalPoints: number;
  level: number;
  badgesCount: number;
  streakDays: number;
}

export const gamificationApi = {
  // Get all available badges
  getAllBadges: () => {
    return api.get<{ success: boolean; data: Badge[]; grouped: Record<string, Badge[]> }>("/gamification/badges");
  },

  // Get badges by category
  getBadgesByCategory: (category: string) => {
    return api.get<{ success: boolean; count: number; data: Badge[] }>(`/gamification/badges/category/${category}`);
  },

  // Get current user's badges
  getMyBadges: () => {
    return api.get<{ success: boolean; count: number; data: UserBadge[] }>("/gamification/my-badges");
  },

  // Get user's stats
  getMyStats: () => {
    return api.get<{ success: boolean; data: any }>("/gamification/my-stats");
  },

  // Check for new badges
  checkForBadges: () => {
    return api.get<{ success: boolean; message: string; data: Badge[] }>("/gamification/check-badges");
  },

  // Get a specific user's badges (public profile)
  getUserBadges: (userId: string) => {
    return api.get<{ success: boolean; data: { badges: UserBadge[]; stats: UserStats; nextLevelPoints: number; pointsToNextLevel: number } }>(`/gamification/users/${userId}/badges`);
  },

  // Get leaderboard
  getLeaderboard: (limit: number = 10, category: string = "points") => {
    return api.get<{ success: boolean; data: LeaderboardEntry[] }>(`/gamification/leaderboard?limit=${limit}&category=${category}`);
  },

  // Get leaderboard by category
  getLeaderboardByCategory: (category: string = "points", limit: number = 10) => {
    return api.get<{ success: boolean; data: LeaderboardEntry[]; category: string }>(`/gamification/leaderboard/${category}?limit=${limit}`);
  },

  // Add XP to user (called from learning page)
  addXP: (userId: string, amount: number, source: string) => {
    return api.post<{ success: boolean; data: any }>("/gamification/add-xp", { userId, amount, source });
  },

  // Update streak
  updateStreak: (userId: string) => {
    return api.post<{ success: boolean; data: any }>("/gamification/update-streak", { userId });
  },

  // Complete a lesson (award XP + check badges)
  completeLesson: (userId: string, lessonId: string) => {
    return api.post<{ success: boolean; data: any; newBadges: Badge[] }>("/gamification/complete-lesson", { userId, lessonId });
  },

  // Pass a quiz (award XP + check badges)
  passQuiz: (userId: string, quizId: string, score: number) => {
    return api.post<{ success: boolean; data: any; newBadges: Badge[] }>("/gamification/pass-quiz", { userId, quizId, score });
  },

  // Complete a course (award XP + check badges)
  completeCourse: (userId: string, courseId: string) => {
    return api.post<{ success: boolean; data: any; newBadges: Badge[] }>("/gamification/complete-course", { userId, courseId });
  },

  // Record learning hours
  recordLearningHours: (userId: string, hours: number) => {
    return api.post<{ success: boolean; data: any }>("/gamification/record-hours", { userId, hours });
  },

  getXPTransactions: (limit = 20, offset = 0) => {
    return api.get<{ success: boolean; count: number; data: any[] }>(`/gamification/xp-transactions?limit=${limit}&offset=${offset}`);
  },

  getUserXPStats: () => {
    return api.get<{ success: boolean; data: any }>("/gamification/xp-stats");
  },

  getDailyQuests: () => {
    return api.get<{ success: boolean; count: number; data: any[] }>("/gamification/daily-quests");
  },

  assignDailyQuests: () => {
    return api.post<{ success: boolean; count: number; data: any[] }>("/gamification/daily-quests/assign");
  },

  getStreakFreezeStatus: () => {
    return api.get<{ success: boolean; data: any }>("/gamification/streak-freeze");
  },

  useStreakFreeze: () => {
    return api.post<{ success: boolean; data: any }>("/gamification/streak-freeze/use");
  },

  resetStreakFreeze: () => {
    return api.post<{ success: boolean; data: any }>("/gamification/streak-freeze/reset");
  },

  adminRecheckUserBadges: (userId: string) => {
    return api.post<{ success: boolean; count: number; data: any[] }>("/gamification/admin/recheck-badges", { userId });
  },

  adminRecheckAllBadges: () => {
    return api.post<{ success: boolean; totalAwarded: number; usersProcessed: number; results: any[] }>("/gamification/admin/recheck-badges/all");
  },

  adminGetUsersEligibleForBadges: () => {
    return api.get<{ success: boolean; count: number; data: any[] }>("/gamification/admin/eligible-badges");
  },
};